import { StudioModal as Modal, StudioDialog as ModalDialog } from '../Workspace/Workspace';
import { useContext, useEffect, useState } from 'react';
import { Box, Button, Chip, Divider, ModalClose, Sheet, Stack, Typography } from '@mui/joy';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import Cookies from 'js-cookie';

import { LayoutContext } from '../../context';
import { APIPaymentBreakdown, APIPaymentBreakdownRefresh } from '../../api';
import PaymentBreakdown, { fmtMoney } from '../Payments/PaymentBreakdown';

export default function ModalPaymentBreakdown() {
  const { modalPaymentBreakdown, setModalPaymentBreakdown, tokenEmployee } = useContext(LayoutContext);
  const token = Cookies.get('auth_token') || tokenEmployee;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const paymentId = modalPaymentBreakdown?.paymentId;
  const open = !!modalPaymentBreakdown?.open;

  const load = async (recalc = false) => {
    if (!paymentId) return;
    setLoading(true);
    setError(null);
    const d = await APIPaymentBreakdown(paymentId, token, recalc);
    if (d?.error) setError(d.error);
    else setData(d);
    setLoading(false);
  };

  useEffect(() => {
    if (open) load(false);
    else {
      setData(null);
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, paymentId]);

  const handleRefresh = async () => {
    setLoading(true);
    const d = await APIPaymentBreakdownRefresh(paymentId, token);
    if (d?.error) {
      setError(
        d.locked
          ? 'Выплата уже проведена — пересчёт запрещён. Историю выплаченных сумм не переписываем.'
          : d.error,
      );
    } else {
      setData(d);
      setError(null);
      modalPaymentBreakdown?.onUpdated?.();
    }
    setLoading(false);
  };

  const close = () => setModalPaymentBreakdown({ open: false, paymentId: null, onUpdated: null });

  const anyPaid = !!(data?.surveyor?.is_paid || data?.draftsman?.is_paid);

  return (
    <Modal open={open} onClose={close} sx={{ zIndex: 10105 }}>
      <ModalDialog sx={{ maxWidth: 760, width: '100%', borderRadius: '18px', overflow: 'auto' }}>
        <ModalClose />
        <Typography level="title-lg" sx={{ mb: 0.5 }}>
          Из чего сумма
        </Typography>

        {data && (
          <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap' }}>
            {data.deal_id && (
              <Chip size="sm" variant="soft" color="primary">
                Сделка №{data.deal_id}
              </Chip>
            )}
            {data.address && (
              <Chip size="sm" variant="soft">
                {data.address}
              </Chip>
            )}
            {data.square != null && (
              <Chip size="sm" variant="soft" color="neutral">
                {data.square} м²
                {data.square_source === 'deal' ? ' (из сделки)' : ''}
                {data.square_source === 'draftsmen_event' ? ' (из чертежа)' : ''}
              </Chip>
            )}
            {data.is_snapshot === false && (
              <Chip size="sm" variant="soft" color="warning">
                расчёт на лету, снимок не сохранён
              </Chip>
            )}
          </Stack>
        )}

        {error && (
          <Sheet variant="soft" color="danger" sx={{ p: 1.25, borderRadius: '12px', mb: 1.5 }}>
            <Typography level="body-sm">{error}</Typography>
          </Sheet>
        )}

        {loading && !data && <Typography level="body-sm">Загрузка…</Typography>}

        {data && (
          <Stack spacing={2}>
            <PaymentBreakdown
              rows={data.surveyor?.rows}
              title={`Обмерщик${data.surveyor?.is_paid ? ' · выплачено' : ''}`}
              accent="#6d28d9"
            />
            <PaymentBreakdown
              rows={data.draftsman?.rows}
              title={`Чертёжник${data.draftsman?.is_paid ? ' · выплачено' : ''}`}
              accent="#0891b2"
            />

            {data.diff && (data.diff.surveyor || data.diff.draftsman) ? (
              <Sheet variant="soft" color="warning" sx={{ p: 1.25, borderRadius: '12px' }}>
                <Typography level="body-sm">
                  Пересчёт отличается от сохранённого: обмерщик {fmtMoney(data.diff.surveyor)} ₽, чертёжник{' '}
                  {fmtMoney(data.diff.draftsman)} ₽
                </Typography>
              </Sheet>
            ) : null}

            <Divider />
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ flexWrap: 'wrap', gap: 1 }}
            >
              <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                {data.computed_at
                  ? `Расчёт от ${new Date(data.computed_at).toLocaleString('ru-RU')}`
                  : 'Снимок расчёта не сохранён'}
              </Typography>
              <Button
                size="sm"
                variant="soft"
                startDecorator={<RestartAltRoundedIcon />}
                loading={loading}
                disabled={anyPaid}
                onClick={handleRefresh}
                sx={{ borderRadius: '10px' }}
              >
                {anyPaid ? 'Пересчёт недоступен (выплачено)' : 'Пересчитать'}
              </Button>
            </Stack>
          </Stack>
        )}
      </ModalDialog>
    </Modal>
  );
}
